import React from 'react';
import Layout from '../../../components/Layout';
import PageMeta from '../../../components/PageMeta';
import { SimplePageLayout } from '../../../types';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAuthProvider } from '../../../providers/Auth';
import { SessionState } from '../../../providers/Auth/utils/types';
import { Error } from '../../../components/Error';
import { getPageTitleByErrorStatusCode } from './utils';
import { Route, useHistory } from 'react-router-dom';
import { ErrorWrapperProps, RouterState } from './types';

interface DynamicErrorProps {
  errTitle?: string;
  errMsg?: string;
  errMetaTitle?: string;
  errLinkText?: string;
  statusCode?: number;
}

/**
 * Error page which can display various error messages
 *
 * @param errMsg - (optional) message to display in the body
 * @param errTitle - (optional) the error title
 * @param errLinkText - (optional) the return link
 * @param statusCode - (optional) the HTTP status code of the error
 * @param errMetaTitle - (optional) the HTML page title
 *
 */
const DynamicError: React.FC<DynamicErrorProps> = ({
  errTitle = `${i18next.t('ns.error_page:error')}`,
  errMsg,
  errMetaTitle,
  errLinkText = `${i18next.t('ns.error_page:404:link_text')}`,
  statusCode,
}: DynamicErrorProps) => {
  const ErrorWrapper: React.FC<ErrorWrapperProps> = ({ message, errTitle, errLinkText }: ErrorWrapperProps) => {
    // Get the router history and check if a homeLink has been passed, otherwise default to landing page
    const history = useHistory();
    const state: RouterState | undefined = history.location.state;
    const homeLink = state && state.homeLink ? state.homeLink : '/';

    return (
      <Error
        errMsg={message}
        errTitle={errTitle}
        errLinkText={errLinkText}
        statusCode={statusCode}
        homeLink={homeLink}
        testId="dynamic-error-page"
      />
    );
  };

  const { t } = useTranslation();
  const { sessionState } = useAuthProvider();
  const isAuthenticated = SessionState[sessionState] === 'valid' ? true : false;

  const layout: SimplePageLayout = {
    main: <Route render={() => <ErrorWrapper message={errMsg} errTitle={errTitle} errLinkText={errLinkText} />} />,
  };

  const metaTitle = getPageTitleByErrorStatusCode(statusCode, t);

  return (
    <>
      <PageMeta title={errMetaTitle ? errMetaTitle : metaTitle || t('ns.error_page:404:page_title')} />
      <Layout showHeader={isAuthenticated ? true : 'simple'} layout={layout} />
    </>
  );
};

export default DynamicError;
